import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { LockKeyhole, Clock3, CheckCircle2, Sparkles, Settings2, Database } from "lucide-react";
import ModuleWorkspacePanel from "@/pages/admin/ModuleWorkspacePanel";

const sectionNames = { wellness: "Wellness", fitness: "Fitness", leisure: "Leisure", dining: "Dining & Service", service: "Guest Services" };
const entitlementLabel = { licensed: "Licensed", unlicensed: "Available", demo_requested: "Demo requested", demo_active: "Demo", demo_expired: "Demo ended", suspended: "Suspended" };

export default function ModulesPanel() {
  const { admin } = useAuth();
  const isDeveloper = admin?.role === "developer";
  const canOperate = ["developer", "admin", "manager", "supervisor", "crew"].includes(admin?.role);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => { try { const r = await api.get("/admin/modules"); setItems(r.data?.items || []); } catch { setItems([]); } };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const out = {};
    for (const m of items) (out[m.category] ||= []).push(m);
    return out;
  }, [items]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not update module"); }
    finally { setBusy(""); }
  };

  const toggle = (m, enabled) => call(m.id, () => api.put(`/admin/modules/${m.id}/operational`, { enabled }), `${m.name} ${enabled ? "enabled" : "disabled"}`);
  const requestDemo = (m) => call(m.id, () => api.post(`/admin/modules/${m.id}/demo-request`, {}), `Demo requested for ${m.name}`);
  const entitlement = (m, state, demo_days) => call(m.id, () => api.put(`/developer/modules/${m.id}/entitlement`, { entitlement: state, demo_days }), `${m.name} updated`);

  if (selected) return <ModuleWorkspacePanel module={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-8" data-testid="modules-panel">
      <div>
        <div className="overline text-accent mb-2">SLX-one Platform</div>
        <h1 className="font-display text-4xl md:text-5xl">Modules</h1>
        <p className="text-muted-foreground mt-3 max-w-3xl">Every module owns its own content and settings here. You can prepare an unlicensed module completely before requesting a demo or purchasing it; guest access remains locked until entitlement is granted and the vessel enables the service.</p>
      </div>

      {Object.entries(groups).map(([category, mods]) => (
        <section key={category} className="space-y-3">
          <div className="overline text-primary/60">{sectionNames[category] || category}</div>
          <div className="grid lg:grid-cols-2 gap-4">
            {mods.map((m) => {
              const usable = ["licensed", "demo_active"].includes(m.entitlement);
              const demoDays = m.demo_expires_at ? Math.max(0, Math.ceil((new Date(m.demo_expires_at) - new Date()) / 86400000)) : null;
              return (
                <div key={m.id} className={`border p-5 bg-card ${usable ? "border-border" : "border-dashed border-border"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-display text-xl">{m.name}</div>
                        <Badge variant={usable ? "default" : "outline"}>{entitlementLabel[m.entitlement] || m.entitlement}</Badge>
                        {m.entitlement === "demo_active" && <Badge variant="outline"><Clock3 className="h-3 w-3 mr-1" />{demoDays}d left</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">{m.description}</p>
                      <div className="text-xs text-muted-foreground mt-3">Engine: {m.engine} · Apple Watch {m.watch ? "ready" : "not used"}</div>
                    </div>
                    {usable && canOperate && <Switch checked={!!m.operational_enabled} disabled={busy === m.id} onCheckedChange={(v) => toggle(m, v)} aria-label={`Enable ${m.name}`} />}
                  </div>

                  <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelected(m)}>
                      {m.id === "spa" ? <Settings2 className="h-4 w-4 mr-2"/> : <Database className="h-4 w-4 mr-2"/>}
                      Content & settings
                    </Button>
                    {!usable && <span className="text-xs text-muted-foreground">Setup available before licence</span>}
                  </div>

                  {!usable && !isDeveloper && (
                    <div className="mt-4 flex items-center gap-3">
                      {m.entitlement === "demo_requested" ? (
                        <div className="text-sm text-muted-foreground flex items-center gap-2"><Clock3 className="h-4 w-4" />Demo request pending</div>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy === m.id || !canOperate} onClick={() => requestDemo(m)}><Sparkles className="h-4 w-4 mr-2" />Request demo</Button>
                      )}
                    </div>
                  )}

                  {isDeveloper && (
                    <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => entitlement(m, "licensed") }><CheckCircle2 className="h-4 w-4 mr-2" />License</Button>
                      <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => entitlement(m, "demo_active", 14)}><Clock3 className="h-4 w-4 mr-2" />14-day demo</Button>
                      <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => entitlement(m, "demo_active", 30)}>30-day demo</Button>
                      <Button size="sm" variant="ghost" disabled={busy === m.id || m.id === "spa"} onClick={() => entitlement(m, "unlicensed") }><LockKeyhole className="h-4 w-4 mr-2" />Unlicense</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
